import React, { ReactNode } from 'react';
import { Box, alpha } from '@mui/material';
import { useFieldVisibility } from '../hooks/useFieldVisibility';
import { FieldPrivacyToggle } from './FieldPrivacyToggle';
import { useWizardAccordionVariant } from './edit-object/WizardAccordionStyleContext';

interface PrivacyFieldContainerProps {
  fieldPath: string;
  children: ReactNode;
  showToggle?: boolean;
  togglePosition?: 'top-right' | 'top-left' | 'inline';
}

/**
 * Visual wrapper for fields with privacy controls.
 * - 2px border: grey for private, green for public
 * - Light background tint for public fields
 * - Toggle positioned in specified corner or inline
 */
export const PrivacyFieldContainer: React.FC<PrivacyFieldContainerProps> = ({
  fieldPath,
  children,
  showToggle = true,
  togglePosition = 'top-right',
}) => {
  const { getFieldVisibility } = useFieldVisibility();
  const variant = useWizardAccordionVariant();
  const visibility = getFieldVisibility(fieldPath);
  const isPublic = visibility === 'public';

  // Artwork Details page suppresses per-field privacy chrome entirely; the
  // page no longer surfaces field-level visibility controls.
  if (variant === 'describe') {
    return <>{children}</>;
  }

  const getToggleStyles = () => {
    switch (togglePosition) {
      case 'top-right':
        return {
          position: 'absolute' as const,
          top: 8,
          right: 8,
          zIndex: 1,
        };
      case 'top-left':
        return {
          position: 'absolute' as const,
          top: 8,
          left: 8,
          zIndex: 1,
        };
      case 'inline':
        return {
          display: 'inline-flex',
          marginLeft: 1,
        };
      default:
        return {};
    }
  };

  // If toggle is inline, wrap differently
  if (togglePosition === 'inline') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1 }}>{children}</Box>
        {showToggle && (
          <Box sx={getToggleStyles()}>
            <FieldPrivacyToggle fieldPath={fieldPath} size="small" />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        border: '2px solid',
        borderColor: isPublic ? 'success.main' : 'grey.300',
        borderRadius: 1,
        p: 2,
        pt: showToggle ? 4 : 2,
        backgroundColor: (theme) =>
          isPublic
            ? alpha(theme.palette.success.light, 0.05)
            : 'transparent',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: isPublic ? 'success.dark' : 'grey.400',
        },
      }}
    >
      {showToggle && (
        <Box sx={getToggleStyles()}>
          <FieldPrivacyToggle fieldPath={fieldPath} showLabel size="small" />
        </Box>
      )}
      {children}
    </Box>
  );
};

export default PrivacyFieldContainer;
